import { createReducer, on } from '@ngrx/store';

import * as BusinessProfilesActions from './business-profiles.actions';
import {
  ExternalBusinessProfile,
  ExternalBusinessProfileAccount,
  TransferProfileResult,
} from './business-profiles.models';

export const businessProfilesFeatureKey = 'businessProfiles';

export interface BusinessProfilesState {
  accounts: ExternalBusinessProfileAccount[];
  accountsLoading: boolean;
  profiles: ExternalBusinessProfile[];
  allProfiles: ExternalBusinessProfile[];
  allProfilesLoading: boolean;
  allProfilesSyncing: boolean;
  loading: boolean;
  syncing: boolean;
  /** AM-4655: an ads-label toggle is in flight (it calls Google, so it is not instant). */
  adsLabelSaving: boolean;
  /**
   * AM-4655: listings the last toggle could not update on Google. The intent IS stored, so this is
   * a "saved but not in step" warning, never an error — the UI must not imply nothing happened.
   */
  adsLabelListingsFailed: number;
  /** AM-4739: a transfer is in flight (it calls Google, so it is not instant). */
  transferring: boolean;
  /**
   * AM-4739: the last completed transfer. Held so the UI can confirm what was preserved and, when
   * `listingUpdated` is false, tell the admin the move landed but still needs a push. Cleared when
   * the dialog closes.
   */
  transferResult: TransferProfileResult | null;
  /**
   * AM-4739: the last transfer failure, deliberately SEPARATE from `error`.
   *
   * Every failure on this slice writes `error` — create, sync, push, ads label, list loads — so a
   * dialog keyed on it would show a message produced by something else entirely, and would light up
   * from a background list refresh that has nothing to do with the transfer. Same reasoning as
   * `organizationsLoadFailed` on the organizations slice (AM-4716).
   */
  transferError: unknown;
  error: unknown;
}

export const initialState: BusinessProfilesState = {
  accounts: [],
  accountsLoading: false,
  profiles: [],
  allProfiles: [],
  allProfilesLoading: false,
  allProfilesSyncing: false,
  loading: false,
  syncing: false,
  adsLabelSaving: false,
  adsLabelListingsFailed: 0,
  transferring: false,
  transferResult: null,
  transferError: null,
  error: null,
};

export const businessProfilesReducer = createReducer(
  initialState,
  on(BusinessProfilesActions.createBusinessProfile, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(BusinessProfilesActions.createBusinessProfileSuccess, (state, { profile }) => ({
    ...state,
    profiles: [...state.profiles, profile],
    loading: false,
  })),
  on(BusinessProfilesActions.createBusinessProfileFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(BusinessProfilesActions.loadBusinessProfilesForShop, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(BusinessProfilesActions.loadBusinessProfilesForShopSuccess, (state, { profiles }) => ({
    ...state,
    profiles,
    loading: false,
  })),
  on(BusinessProfilesActions.loadBusinessProfilesForShopFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(BusinessProfilesActions.syncBusinessProfile, BusinessProfilesActions.pushBusinessProfile, (state) => ({
    ...state,
    syncing: true,
    error: null,
  })),
  on(
    BusinessProfilesActions.syncBusinessProfileSuccess,
    BusinessProfilesActions.pushBusinessProfileSuccess,
    (state) => ({
      ...state,
      syncing: false,
    }),
  ),
  on(
    BusinessProfilesActions.syncBusinessProfileFailure,
    BusinessProfilesActions.pushBusinessProfileFailure,
    (state, { error }) => ({
      ...state,
      syncing: false,
      error,
    }),
  ),
  on(BusinessProfilesActions.loadAllProfiles, (state) => ({
    ...state,
    allProfilesLoading: true,
    error: null,
  })),
  on(BusinessProfilesActions.loadAllProfilesSuccess, (state, { profiles }) => ({
    ...state,
    allProfiles: profiles,
    allProfilesLoading: false,
  })),
  on(BusinessProfilesActions.loadAllProfilesFailure, (state, { error }) => ({
    ...state,
    error,
    allProfilesLoading: false,
  })),
  on(BusinessProfilesActions.syncAllProfiles, (state) => ({
    ...state,
    allProfilesSyncing: true,
    error: null,
  })),
  /**
   * AM-4734: deliberately does NOT write `allProfiles`.
   *
   * `sync_all` returns raw profile documents, not the enriched list items `listAll` produces — no
   * `coordinates`, `shopName` or `adsLabelEnabled`. Overwriting the list with them used to blank
   * those columns; now that the address search FILTERS on `distanceMetres`, it would instead make
   * the ~118 rows that depend on the shop-coordinate fallback vanish from a radius result, so an
   * admin would read "nothing within 500 m" when something is there. A wrong answer, not a gap.
   *
   * `reloadAfterSyncAll$` refetches the list instead, so the store only ever holds the enriched
   * shape. (No caller dispatches `syncAllProfiles` today — this is closed before it opens.)
   */
  on(BusinessProfilesActions.syncAllProfilesSuccess, (state) => ({
    ...state,
    allProfilesSyncing: false,
  })),
  on(BusinessProfilesActions.syncAllProfilesFailure, (state, { error }) => ({
    ...state,
    error,
    allProfilesSyncing: false,
  })),
  on(BusinessProfilesActions.loadAccounts, (state) => ({
    ...state,
    accountsLoading: true,
    error: null,
  })),
  on(BusinessProfilesActions.loadAccountsSuccess, (state, { accounts }) => ({
    ...state,
    accounts,
    accountsLoading: false,
  })),
  on(BusinessProfilesActions.loadAccountsFailure, (state, { error }) => ({
    ...state,
    error,
    accountsLoading: false,
  })),
  on(BusinessProfilesActions.seedAccounts, (state) => ({
    ...state,
    accountsLoading: true,
    error: null,
  })),
  on(BusinessProfilesActions.seedAccountsSuccess, (state, { accounts }) => ({
    ...state,
    accounts,
    accountsLoading: false,
  })),
  on(BusinessProfilesActions.seedAccountsFailure, (state, { error }) => ({
    ...state,
    error,
    accountsLoading: false,
  })),
  on(BusinessProfilesActions.setAdsLabelEnabled, (state) => ({
    ...state,
    adsLabelSaving: true,
    adsLabelListingsFailed: 0,
    error: null,
  })),
  on(BusinessProfilesActions.setAdsLabelEnabledSuccess, (state, { result }) => ({
    ...state,
    adsLabelSaving: false,
    adsLabelListingsFailed: result.listingsFailed ?? 0,
  })),
  on(BusinessProfilesActions.setAdsLabelEnabledFailure, (state, { error }) => ({
    ...state,
    adsLabelSaving: false,
    error,
  })),
  on(BusinessProfilesActions.transferProfile, (state) => ({
    ...state,
    transferring: true,
    transferResult: null,
    transferError: null,
  })),
  on(BusinessProfilesActions.transferProfileSuccess, (state, { result }) => ({
    ...state,
    transferring: false,
    transferResult: result,
  })),
  on(BusinessProfilesActions.transferProfileFailure, (state, { error }) => ({
    ...state,
    transferring: false,
    transferError: error,
  })),
  on(BusinessProfilesActions.clearTransferResult, (state) => ({
    ...state,
    transferResult: null,
    transferError: null,
  })),
  on(BusinessProfilesActions.clearBusinessProfilesState, () => initialState),
);
